import { post } from '@xmatters/redux/services/httpService';
import cloneDeep from 'lodash/cloneDeep';
import { timeout } from '@xmatters/redux/actions/session';
import { SESSION_TIMEOUT_ERROR } from '@xmatters/common/constants/commonStatus';
import { convertStripeError, poll, serverError } from './util';

export const GET_UPGRADE_PLANS = 'GET_UPGRADE_PLANS';
export const GET_UPGRADE_PLANS_START = `${GET_UPGRADE_PLANS}_START`;
export const GET_UPGRADE_PLANS_SUCCESS = `${GET_UPGRADE_PLANS}_SUCCESS`;
export const GET_UPGRADE_PLANS_FAIL = `${GET_UPGRADE_PLANS}_FAIL`;

export const GET_STRIPE_KEY = 'GET_STRIPE_KEY';
export const GET_STRIPE_KEY_START = `${GET_STRIPE_KEY}_START`;
export const GET_STRIPE_KEY_SUCCESS = `${GET_STRIPE_KEY}_SUCCESS`;
export const GET_STRIPE_KEY_FAIL = `${GET_STRIPE_KEY}_FAIL`;

export const CALCULATE_TAX = 'CALCULATE_TAX';
export const CALCULATE_TAX_START = `${CALCULATE_TAX}_START`;
export const CALCULATE_TAX_SUCCESS = `${CALCULATE_TAX}_SUCCESS`;
export const CALCULATE_TAX_FAIL = `${CALCULATE_TAX}_FAIL`;

export const CREATE_BILLING = 'CREATE_BILLING';
export const CREATE_BILLING_START = `${CREATE_BILLING}_START`;
const CREATE_BILLING_STEP1_SUCCESS = `${CREATE_BILLING}_STEP1_SUCCESS`;
const CREATE_BILLING_STEP2_SUCCESS = `${CREATE_BILLING}_STEP2_SUCCESS`;
const CREATE_BILLING_STEP3_SUCCESS = `${CREATE_BILLING}_STEP3_SUCCESS`;
const CREATE_BILLING_STEP4_SUCCESS = `${CREATE_BILLING}_STEP4_SUCCESS`;
export const CREATE_BILLING_SUCCESS = `${CREATE_BILLING}_SUCCESS`;
export const CREATE_BILLING_FAIL = `${CREATE_BILLING}_FAIL`;

export const SET_UPGRADE_COMPLETE = 'SET_UPGRADE_COMPLETE';

const baseUrl = '/webapi/provision/xm/purchase/api';
const plansPath = '/plans';
const stripeKeyPath = '/constants';
const taxPath = '/tax';
const upgradePath = '/upgrade';

const stripeFatalError = 'STRIPE_FATAL_ERROR';

export const setUpgradeComplete = currentOrganizationId => ({
  type: SET_UPGRADE_COMPLETE,
  payload: currentOrganizationId,
});

export const getUpgradePlans = (props = {}) => {
  const { isSubscription, country } = props;
  const params = {
    category: isSubscription ? 'ONE_YEAR_SUBSCRIPTION' : 'ONE_YEAR',
    country,
  };
  return {
    type: GET_UPGRADE_PLANS,
    meta: {
      xmApi: true,
      method: 'get',
      uri: `${baseUrl}${plansPath}`,
      params,
    },
  };
};

export const getStripeKey = () => ({
  type: GET_STRIPE_KEY,
  meta: {
    xmApi: true,
    method: 'get',
    uri: `${baseUrl}${stripeKeyPath}`,
  },
});

export const calculateTax = purchaseInfo => ({
  type: CALCULATE_TAX,
  meta: {
    xmApi: true,
    method: 'post',
    uri: `${baseUrl}${taxPath}`,
    params: purchaseInfo,
  },
});

// flow: https://xmatters.atlassian.net/wiki/spaces/SERV/pages/6661332/Provisioning+Service+HTTP+API#ProvisioningServiceHTTPAPI-Purchasing
export const createBilling = (
  stripe,
  stripeParams,
  paymentIntentParams,
  orderInfo,
  chargeParams,
) => {
  return dispatch => {
    dispatch({ type: CREATE_BILLING_START });
    let stripeClientSecret;
    let purchaseId;
    let orderDetails;

    return post({
      uri: `${baseUrl}${upgradePath}`,
      params: paymentIntentParams,
    })
      .catch(err => {
        if (err.code === 401) {
          dispatch(timeout({}));
          throw new Error(SESSION_TIMEOUT_ERROR);
        }
        dispatch({ type: CREATE_BILLING_FAIL });
        throw new Error(serverError);
      })
      .then(res => {
        stripeClientSecret = res.stripeClientSecret;
        purchaseId = res.purchaseId;
        dispatch({ type: CREATE_BILLING_STEP1_SUCCESS });

        return poll(`${baseUrl}/purchases/${purchaseId}`).catch(err => {
          dispatch({ type: CREATE_BILLING_FAIL });
          throw new Error(err.message);
        });
      })
      .then(res => {
        orderDetails = cloneDeep(orderInfo);
        orderDetails.orderNumber = res.processData.orderNumber;
        orderDetails.planExpiry = res.processData.planExpiry;
        orderDetails.planRenewal = res.processData.planRenewal;
        orderDetails.charge = { ...chargeParams };
        dispatch({
          type: CREATE_BILLING_STEP2_SUCCESS,
        });

        return stripe.createPaymentMethod(stripeParams).catch(() => {
          dispatch({ type: CREATE_BILLING_FAIL });
          throw new Error(stripeFatalError);
        });
      })
      .then(res => {
        if (res.error) {
          dispatch({ type: CREATE_BILLING_FAIL });
          throw new Error(convertStripeError(res.error));
        }
        const creditCard = res.paymentMethod.card;
        orderDetails.cardInfo = {
          brand: creditCard.brand,
          ending: creditCard.last4,
        };
        dispatch({ type: CREATE_BILLING_STEP3_SUCCESS });

        // This POST should be very closely tied to the `.confirmCardPayment`
        // call below.
        post({
          uri: `${baseUrl}/purchases/${purchaseId}/start-payment`,
        }).catch(err => {
          dispatch({ type: CREATE_BILLING_FAIL });
          throw new Error(err.message);
        });

        return stripe
          .confirmCardPayment(stripeClientSecret, {
            payment_method: stripeParams,
          })
          .catch(() => {
            dispatch({ type: CREATE_BILLING_FAIL });
            throw new Error(stripeFatalError);
          });
      })
      .then(res => {
        if (res.error) {
          dispatch({ type: CREATE_BILLING_FAIL });
          post({
            uri: `${baseUrl}/purchases/${purchaseId}/confirm-payment`,
            params: {
              result: 'failure',
            },
          });
          throw new Error(convertStripeError(res.error));
        }
        dispatch({ type: CREATE_BILLING_STEP4_SUCCESS });

        return post({
          uri: `${baseUrl}/purchases/${purchaseId}/confirm-payment`,
          params: {
            result: 'success',
          },
        }).catch(err => {
          if (err.code === 410) {
            // Can safely be ignored, Stripe has already called back
            return Promise.resolve('done confirm-payment');
          }
          dispatch({ type: CREATE_BILLING_FAIL });
          throw new Error(err.message);
        });
      })
      .then(() => {
        dispatch({
          type: CREATE_BILLING_SUCCESS,
          payload: orderDetails,
        });
        return 'resolved';
      });
  };
};
